/**
 * Atajos del workspace de analisis. La resolucion es pura (facil de probar); el efecto
 * secundario vive en `useWorkspaceShortcuts`.
 */

export const isEditableTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

export type WorkspaceShortcutAction =
  | { readonly type: 'undo' }
  | { readonly type: 'redo' }
  | { readonly type: 'delete-card'; readonly cardId: string }
  | { readonly type: 'duplicate-card'; readonly cardId: string }
  | { readonly type: 'copy-card'; readonly cardId: string }
  | {
      readonly type: 'paste-card';
      readonly cardId: string;
      readonly afterCardId: string | null;
    }
  | { readonly type: 'add-card' }
  | { readonly type: 'toggle-maximize'; readonly cardId: string }
  | { readonly type: 'clear-maximize'; readonly cardId: string }
  | { readonly type: 'deselect' }
  | { readonly type: 'toggle-explorer' }
  | { readonly type: 'toggle-properties' }
  | { readonly type: 'toggle-ai-panel' };

export interface ShortcutEvent {
  readonly key: string;
  readonly code?: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly defaultPrevented: boolean;
  readonly target: EventTarget | null;
}

export interface ShortcutContext {
  readonly selectedCardId: string | null;
  readonly maximizedCardId: string | null;
  readonly copiedCardId: string | null;
}

/**
 * Traduce un `keydown` a una accion del workspace, o `null` si no aplica.
 * No ejecuta nada: el llamador decide preventDefault y el efecto.
 */
export function resolveWorkspaceShortcut(
  event: ShortcutEvent,
  context: ShortcutContext,
): WorkspaceShortcutAction | null {
  if (event.defaultPrevented) return null;
  const mod = event.ctrlKey || event.metaKey;
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  const editable = isEditableTarget(event.target);

  if (mod && !editable) {
    if (key === 'z' && !event.shiftKey && !event.altKey) return { type: 'undo' };
    if ((key === 'y' && !event.shiftKey) || (key === 'z' && event.shiftKey))
      return { type: 'redo' };
    if (key === 'd' && !event.shiftKey && !event.altKey && context.selectedCardId !== null) {
      return { type: 'duplicate-card', cardId: context.selectedCardId };
    }
    if (key === 'c' && !event.shiftKey && !event.altKey && context.selectedCardId !== null) {
      return { type: 'copy-card', cardId: context.selectedCardId };
    }
    if (key === 'v' && !event.shiftKey && !event.altKey && context.copiedCardId !== null) {
      return {
        type: 'paste-card',
        cardId: context.copiedCardId,
        afterCardId: context.selectedCardId,
      };
    }
    if (key === 'n' && event.shiftKey && !event.altKey) return { type: 'add-card' };
    if (key === 'm' && !event.shiftKey && !event.altKey && context.selectedCardId !== null) {
      return { type: 'toggle-maximize', cardId: context.selectedCardId };
    }
    if (key === 'b' && !event.shiftKey && !event.altKey) return { type: 'toggle-explorer' };
    if (key === '\\' && !event.shiftKey && !event.altKey) return { type: 'toggle-properties' };
    if (key === 'j' && !event.shiftKey && !event.altKey) return { type: 'toggle-ai-panel' };
    return null;
  }

  if (editable || mod || event.altKey) return null;

  if (key === 'Escape') {
    if (context.maximizedCardId !== null) {
      return { type: 'clear-maximize', cardId: context.maximizedCardId };
    }
    if (context.selectedCardId !== null) return { type: 'deselect' };
    return null;
  }

  if ((key === 'Delete' || key === 'Backspace') && context.selectedCardId !== null) {
    return { type: 'delete-card', cardId: context.selectedCardId };
  }

  return null;
}
